import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, FormField, TextInput, List, Text, Tabs, Tab, ResponsiveContext } from "grommet";
import React from "react";
import StyledModal from "../components/StyledModel";
import { TeamRow } from "../teams/TeamsManagement";
import { createTeam, CreateTeamRequest, getTeam, getTeamMembers, searchTeams, TeamViewModel } from "../teams/TeamsStore";
import { usePermission, useUser } from "../account/AccountStore";
import { addParticipant, AddTournamentParticipantRequest, AliasTournamentParticipantViewModel, removeParticipant, RemoveTournamentParticipantRequest, TournamentParticipantViewModel, updateParticipant, UpdateTournamentParticipantRequest } from "./TournamentsStore";
import Avatar from "react-avatar";
import { AliasViewModel, createAliasAndUser, CreateAliasRequest, getAlias, searchAliases } from "../aliases/AliasStore";
import SkeletonWrapper from "../components/SkeletonWrapper";
import { TeamName } from "../teams/TeamName";
import { AliasCard } from "../aliases/AliasCard";
import { AliasRow } from "../aliases/AliasRow";
import { AliasTile } from "../aliases/AliasTile";
import { debounce, set } from "lodash";
import { Organization } from "../organizations/OrganizationStore";
import OrganizationSearch from "../organizations/OrganizationSearch";
import AliasDetails from "../aliases/AliasDetails";
import TeamDetails from "../teams/TeamDetails";
import { NavigationButton } from "../components/NavigationButton";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { TournamentInvites } from "./TournamentInvites";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const chain = <FontAwesomeIcon icon={["fad", "chain"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;
const message = <FontAwesomeIcon icon={["fad", "message"]}></FontAwesomeIcon>;
const bell = <FontAwesomeIcon icon={["fad", "bell"]}></FontAwesomeIcon>;

const TournamentParticipants = ({ tournament, participants, onChange }) => {
    let { pathname } = useLocation();
    let navigate = useNavigate();

    React.useEffect(() => {
        if (tournament.id) {
            if (pathname == `/tournaments/${tournament.id}/teams`) {
                navigate(`/tournaments/${tournament.id}/teams/participants`, { replace: true });
            }
        }
    }, [pathname, tournament.id]);

    const [isLoading, setIsLoading] = React.useState(false);

    const hasWritePermission = usePermission("sport:tournaments.write");
    const user = useUser();

    const [showAddParticipantModal, setShowAddParticipantModal] = React.useState(false);
    const [showRemoveParticipantModal, setShowRemoveParticipantModal] = React.useState(false);

    const [participantToRemove, setParticipantToRemove] = React.useState({} as TeamViewModel);

    const [participantCreateError, setParticipantCreateError] = React.useState("");
    const [participantRemoveError, setParticipantRemoveError] = React.useState("");
    const [organization, setOrganization] = React.useState({} as Organization);

    const addParticipantToTournament = (team: TeamViewModel, members: AliasViewModel[]) => {
        setParticipantCreateError("");
        setIsLoading(true);
        return addParticipant({ tournamentID: tournament.id, teamID: team.id, aliases: members.map((a) => a.id) } as AddTournamentParticipantRequest).then((response) => {
            if (response.success) {
                onChange();
            } else {
                setParticipantCreateError(response.errors.join("\n"));
                setIsLoading(false);
            }
            return response;
        });
    };

    const startRemoveParticipant = (participant: TeamViewModel) => {
        setParticipantToRemove(participant);
        setShowRemoveParticipantModal(true);
    };

    const removeParticipantFromTournament = () => {
        setParticipantRemoveError("");
        setIsLoading(true);
        removeParticipant({ tournamentID: tournament.id, teamID: participantToRemove.id } as RemoveTournamentParticipantRequest).then((response) => {
            if (response.success) {
                onChange();
                setShowRemoveParticipantModal(false);
            } else {
                setParticipantRemoveError(response.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };
    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);
    let [teamFilter, setTeamFilter] = React.useState("");

    const searchTeam = (filter, organizationID) => {
        searchTeams(filter, organizationID, 0, 10).then((response) => {
            setTeamSearchResult(response);
        });
    };

    const debounceSearchTeam = React.useMemo(() => debounce(searchTeam, 300), []);
    React.useEffect(() => {
        debounceSearchTeam(teamFilter, organization.id);
    }, [teamFilter, organization.id]);

    const [participantLoading, setParticipantLoading] = React.useState(false);
    const [participantToAdd, setParticipantToAdd] = React.useState({} as TeamViewModel);
    const [teamAliasToAdd, setTeamAliasToAdd] = React.useState([] as AliasViewModel[]);

    const selectParticipantToAdd = (team: TeamViewModel) => {
        setParticipantToAdd(team);
        setParticipantLoading(true);
        getTeamMembers(team.id).then((response) => {
            setTeamAliasToAdd(response.data.result.map((r) => r.alias));
            setParticipantLoading(false);
        });
    };

    const [participantToEdit, setParticipantToEdit] = React.useState({} as TournamentParticipantViewModel);
    const [teamAliasToEdit, setTeamAliasToEdit] = React.useState([] as AliasTournamentParticipantViewModel[]);
    const [showParticipantEditModal, setShowParticipantEditModal] = React.useState(false);

    const startEditParticipant = (participant: TournamentParticipantViewModel) => {
        setParticipantToEdit(participant);
        setTeamAliasToEdit(participant.aliases);
        setTeamAliasToAdd([]);
        setShowParticipantEditModal(true);
    };

    let [userSearchResult, setUserSearchResult] = React.useState([] as Array<AliasViewModel>);

    const searchAlias = (filter, organizationID) => {
        searchAliases(filter, organizationID, 0, 10).then((response) => {
            setUserSearchResult(response);
        });
    };

    const debounceSearchAlias = React.useMemo(() => debounce(searchAlias, 300), []);

    let [userFilter, setUserFilter] = React.useState("");
    const [role, setRole] = React.useState("Member");

    const [memberCreateError, setMemberCreateError] = React.useState("");

    React.useEffect(() => {
        debounceSearchAlias(userFilter, organization.id);
    }, [userFilter, organization.id]);

    const [showAddAliasModal, setShowAddAliasModal] = React.useState(false);

    const addPlayer = (alias: AliasViewModel) => {
        setMemberCreateError("");
        setTeamAliasToAdd([...teamAliasToAdd, alias]);
        setShowAddAliasModal(false);
    };

    const [alias, setAlias] = React.useState({} as CreateAliasRequest);
    const [isCreating, setIsCreating] = React.useState(false);
    const createUser = () => {
        setIsCreating(true);
        createAliasAndUser({
            ...alias,
            organizationID: organization.id,
        } as CreateAliasRequest).then((response) => {
            if (response.success) {
                let aliases = teamAliasToEdit.map((a) => a.aliasID).concat(teamAliasToAdd.map((a) => a.id));
                aliases.push(response.aliasID);
                updateParticipants(aliases).then((response) => {
                    if (response.success) {
                        onChange();
                        setShowAddAliasModal(false);
                    }
                });

                setAlias({} as CreateAliasRequest);
                setMemberCreateError("");
            } else {
                setMemberCreateError(response.errors.join("\n"));
            }
            setIsCreating(false);
        });
    };

    const doCreateTeam = () => {
        setIsCreating(true);
        createTeam({
            ...team,
            organizationID: organization.id,
        } as CreateTeamRequest).then((response) => {
            if (response.success) {
                getTeam(response.teamID).then((teamResponse) => {
                    addParticipantToTournament(teamResponse.data.result, []).then((response) => {
                        if (response.success) {
                            setShowAddParticipantModal(false);
                        } else {
                            setParticipantCreateError(response.errors.join("\n"));
                        }
                    });
                });
            } else {
                setParticipantCreateError(response.errors.join("\n"));
            }
            setIsCreating(false);
        });
    };

    const updateParticipants = (aliases) => {
        return updateParticipant({
            participantID: participantToEdit.id,
            aliasIDs: aliases,
            seed: participantToEdit.seed,
        } as UpdateTournamentParticipantRequest).then((response) => {
            if (response.success) {
            } else {
                setParticipantCreateError(response.errors.join("\n"));
            }
            return response;
        });
    };

    const [team, setTeam] = React.useState({} as CreateTeamRequest);

    const size = React.useContext(ResponsiveContext);

    return (
        <>
            {user.hasRole("Admin") && (
                <Box direction="row" align="center" justify="center" gap="medium" wrap>
                    <NavigationButton href={`/tournaments/${tournament.id}/teams/participants`} text={"Participants"}></NavigationButton>
                    <NavigationButton href={`/tournaments/${tournament.id}/teams/invites`} text={"Invitations"}></NavigationButton>
                </Box>
            )}
            <Routes>
                <Route
                    path="participants"
                    element={
                        <>
                            <Box pad={"small"} gap="small">
                                {participants.map((participant) => (
                                    <TeamRow team={participant.team} key={participant.id}>
                                        <Box direction={size == "small" ? "column" : "row"} gap="small">
                                            {participant.aliases.map((aliasParticipant) => (
                                                <AliasTile key={aliasParticipant.id} alias={aliasParticipant.alias}></AliasTile>
                                            ))}
                                            {hasWritePermission && !tournament.locked && (
                                                <Box gap="small">
                                                    <Button label="Manage Players" icon={edit} onClick={() => startEditParticipant(participant)} color="status-ok"></Button>
                                                    <Button label="Remove Team" icon={xmark} onClick={() => startRemoveParticipant(participant.team)} color="status-critical"></Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </TeamRow>
                                ))}
                            </Box>
                            {hasWritePermission && !tournament.locked && <Button icon={plus} label="Add Participant" onClick={() => setShowAddParticipantModal(true)}></Button>}
                        </>
                    }
                />
                <Route
                    path="invites"
                    element={
                        <>
                            <TournamentInvites tournament={tournament} onChange={onChange}></TournamentInvites>
                        </>
                    }
                />
            </Routes>

            <StyledModal
                isOpen={showAddParticipantModal}
                setIsOpen={(open) => {
                    setShowAddParticipantModal(open);
                    setParticipantToAdd({} as TeamViewModel);
                    setTeamAliasToAdd([]);
                }}
            >
                <Box width={"medium"}>
                    <SkeletonWrapper loading={participantLoading}>
                        {!participantToAdd.id && (
                            <>
                                <OrganizationSearch onSelect={(organization) => setOrganization(organization)}></OrganizationSearch>
                                <Tabs>
                                    <Tab title="Search">
                                        <FormField label="Search">
                                            <TextInput size="xs" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} />
                                        </FormField>
                                        <List
                                            pad="xs"
                                            data={teamSearchResult}
                                            itemKey="id"
                                            primaryKey={(team: TeamViewModel) => (
                                                <Text size="small" key={team.id}>
                                                    <Avatar name={team.name} src={team.logoUri} round size="25px"></Avatar> {team.name}
                                                </Text>
                                            )}
                                            action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => selectParticipantToAdd(item)} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                                        ></List>
                                    </Tab>
                                    <Tab title="Create">
                                        <TeamDetails team={team} isBusy={isCreating} onChange={(team) => setTeam(team)} size="small"></TeamDetails>
                                        <Button primary onClick={doCreateTeam} label="Create"></Button>
                                    </Tab>
                                </Tabs>
                            </>
                        )}
                        {participantToAdd.id && (
                            <>
                                <Text>Confirm Team Members for:</Text>
                                <Box direction="row" gap="small">
                                    <TeamRow team={participantToAdd}>
                                        <Box></Box>
                                    </TeamRow>
                                </Box>
                                <Text>Players</Text>

                                <Box direction="row" gap="small">
                                    {teamAliasToAdd.map((alias) => (
                                        <AliasRow alias={alias} key={alias.id}>
                                            <Box></Box>
                                        </AliasRow>
                                    ))}
                                </Box>
                                <Box direction="row" gap="small">
                                    <Button primary label="Add" onClick={() => addParticipantToTournament(participantToAdd, teamAliasToAdd)}></Button>
                                </Box>
                            </>
                        )}
                        <Text>
                            <pre>{participantCreateError}</pre>
                        </Text>
                    </SkeletonWrapper>
                </Box>
            </StyledModal>

            <StyledModal
                isOpen={showParticipantEditModal}
                setIsOpen={(open) => {
                    setShowParticipantEditModal(open);
                    setParticipantToEdit({} as TournamentParticipantViewModel);
                    setTeamAliasToEdit([]);
                    setTeamAliasToAdd([]);
                }}
            >
                <Box width={"medium"}>
                    <SkeletonWrapper loading={participantLoading}>
                        <Text>Manage Team Members for:</Text>
                        <Box direction="row" gap="small">
                            <TeamRow team={participantToEdit.team}>
                                <Box></Box>
                            </TeamRow>
                        </Box>
                        <Text>Players</Text>

                        <Box gap="small">
                            {teamAliasToEdit.map((participant) => (
                                <AliasRow alias={participant.alias} key={participant.id}>
                                    <Box justify="end" fill>
                                        <Button
                                            color={"status-critical"}
                                            icon={xmark}
                                            label="Remove"
                                            onClick={() => {
                                                setTeamAliasToEdit(teamAliasToEdit.filter((a) => a.id !== participant.id));
                                            }}
                                        ></Button>
                                    </Box>
                                </AliasRow>
                            ))}
                            {teamAliasToAdd.map((alias) => (
                                <AliasRow alias={alias} key={alias.id}>
                                    <Box justify="end" fill>
                                        <Button
                                            color={"status-critical"}
                                            icon={xmark}
                                            label="Remove"
                                            onClick={() => {
                                                setTeamAliasToAdd(teamAliasToAdd.filter((a) => a.id !== alias.id));
                                            }}
                                        ></Button>
                                    </Box>
                                </AliasRow>
                            ))}
                        </Box>
                        <Box direction="row" gap="small">
                            <Button
                                label="Add Player"
                                onClick={() => {
                                    setShowAddAliasModal(true);
                                }}
                            ></Button>
                            <Button
                                primary
                                label="Update"
                                onClick={() =>
                                    updateParticipants(teamAliasToEdit.map((a) => a.aliasID).concat(teamAliasToAdd.map((a) => a.id))).then((response) => {
                                        if (response.success) {
                                            onChange();
                                            setShowParticipantEditModal(false);
                                        }
                                    })
                                }
                            ></Button>
                        </Box>

                        <Text>
                            <pre>{participantCreateError}</pre>
                        </Text>
                    </SkeletonWrapper>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showAddAliasModal} setIsOpen={setShowAddAliasModal}>
                <Box width={"medium"}>
                    <OrganizationSearch onSelect={(organization) => setOrganization(organization)}></OrganizationSearch>
                    <Tabs>
                        <Tab title="Search">
                            <FormField label="Search">
                                <TextInput size="xs" value={userFilter} onChange={(e) => setUserFilter(e.target.value)} />
                            </FormField>
                            <List
                                pad="xs"
                                data={userSearchResult}
                                itemKey="id"
                                primaryKey={(member: AliasViewModel) => (
                                    <Text size="small" key={member.id}>
                                        <Avatar name={member.name} src={member.photoUri} round size="25px"></Avatar> {member.name}
                                    </Text>
                                )}
                                action={(item, index) => <Button hoverIndicator key={item.id} size="small" secondary onClick={() => addPlayer(item)} disabled={!role} icon={<FontAwesomeIcon icon={["fad", "caret-right"]}></FontAwesomeIcon>} />}
                            ></List>
                        </Tab>
                        <Tab title="Create">
                            <AliasDetails alias={alias} isBusy={isCreating} onChange={(alias) => setAlias(alias)} size="small"></AliasDetails>
                            <Button primary onClick={createUser} label="Create"></Button>
                        </Tab>
                    </Tabs>

                    <Text>
                        <pre>{memberCreateError}</pre>
                    </Text>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showRemoveParticipantModal} setIsOpen={setShowRemoveParticipantModal}>
                <Box width={"medium"}>
                    <Text>Are you sure you want to remove this participant?</Text>
                    <Text>
                        <pre>{participantRemoveError}</pre>
                    </Text>
                    <Box direction="row" justify="end" pad={"small"} gap="small">
                        <Button onClick={() => setShowRemoveParticipantModal(false)} label="Cancel"></Button>
                        <Button primary onClick={() => removeParticipantFromTournament()} label="Remove" color="status-critical"></Button>
                    </Box>
                </Box>
            </StyledModal>
        </>
    );
};

export default TournamentParticipants;
